/** @format */

import { memo } from 'react'
import { CopyBlock, shadesOfPurple } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import insert_adjacentImg from 'assets/images/insertadjacent.png'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>4 - Создание и удаление узлов</h2>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.1 - Работа с DOM-узлами</h3>
      <p className={styles.description}>
        Используя DOM API мы можем не только выбирать уже существующие, но и удалять, а так же создавать новые элементы,
        после чего добавлять их в документ.
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.1.1 - Создание</h4>
        <CopyBlock text={`document.createElement(tagName)`} language="html" showLineNumbers theme={shadesOfPurple} />
        <p className={styles.description}>
          Создает HTML-элемент по указаному имени тега и возвращает ссылку на него как результат своего выполнения.{' '}
          <code className={styles.code}>tagName</code> это строка, указывающая тип создаваемого элемента. Элемент
          создается в памяти, в DOM его еще нет.{' '}
        </p>
        <CopyBlock
          text={`const heading = document.createElement('h1');
console.log(heading); // <h1></h1>

heading.textContent = 'This is a heading';
console.log(heading); // <h1>This is a heading</h1>

const image = document.createElement('img');
image.setAttribute('src', 'https://placeimg.com/640/480/nature');
image.setAttribute('alt', 'nature');

console.log(image); // <img src="https://placeimg.com/640/480/nature" alt="nature">`}
          language="html"
          showLineNumbers
          theme={shadesOfPurple}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.1.2 - Добавление</h4>
        <p className={styles.description}>
          Чтобы созданный элемент был отображен на странице, его необходимо добавить к уже существующему элементу в DOM.
          Допустим, что добавляем в некий элемент <code className={styles.code}>parentElem</code>, для этого есть
          методы.{' '}
        </p>
        <CopyBlock text={`parentElem.appendChild(elem)`} language="html" showLineNumbers theme={shadesOfPurple} />
        <p className={styles.description}>
          Добавляет <code className={styles.code}>elem</code> в конец дочерних элементов{' '}
          <code className={styles.code}>parentElem</code>.
        </p>
        <CopyBlock
          text={`parentElem.insertBefore(elem, nextSibling)`}
          language="html"
          showLineNumbers
          theme={shadesOfPurple}
        />
        <p className={styles.description}>
          Добавляет <code className={styles.code}>elem</code> в коллекцию детей{' '}
          <code className={styles.code}>parentElem</code>, перед элементом{' '}
          <code className={styles.code}>nextSibling</code>. Если вторым аргументом указать{' '}
          <code className={styles.code}>null</code>, тогда <code className={styles.code}>insertBefore</code> сработает
          как <code className={styles.code}>appendChild</code>.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="module-08-append-insert"
          src="https://codepen.io/goit-fe-adv/embed/zdgLmN?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
        <p className={styles.notificationInfo}>
          Если элемент для вставки это существующий узел, то он изымается из своего старого места и ставится на новое.
          Отсюда вытекает правило — один и тот же узел не может быть одновременно в двух местах.{' '}
        </p>
        <section className={styles.subsection}>
          <h4 className={styles.h4}>4.1.2.1 - Методы append/prepend, before/after, replaceWith</h4>
          <p className={styles.description}>
            Есть методы которые позволяют вставить что угодно и куда угодно. Во всех этих методах,{' '}
            <code className={styles.code}>nodes</code> – DOM-узлы или строки, в любом сочетании и количестве. Причём
            строки вставляются как текстовые узлы.{' '}
          </p>
          <ul className={styles.list}>
            <li className={styles.list__item}>
              <code className={styles.code}>elem.append(nodes)</code> - добавляет{' '}
              <code className={styles.code}>nodes</code> в конец <code className={styles.code}>elem</code>
            </li>
            <li className={styles.list__item}>
              <code className={styles.code}>elem.prepend(nodes)</code> - добавляет{' '}
              <code className={styles.code}>nodes</code> в начало <code className={styles.code}>elem</code>
            </li>
            <li className={styles.list__item}>
              <code className={styles.code}>elem.after(nodes)</code> - добавляет{' '}
              <code className={styles.code}>nodes</code> после узла <code className={styles.code}>elem</code>
            </li>
            <li className={styles.list__item}>
              <code className={styles.code}>elem.before(nodes)</code> - добавляет{' '}
              <code className={styles.code}>nodes</code> перед узлом <code className={styles.code}>elem</code>
            </li>
            <li className={styles.list__item}>
              <code className={styles.code}>elem.replaceWith(nodes)</code> - добавляет{' '}
              <code className={styles.code}>nodes</code> вместо <code className={styles.code}>elem</code>
            </li>
          </ul>
          <iframe
            height="300"
            scrolling="no"
            title="module-08-append-prepend"
            src="https://codepen.io/goit-fe-adv/embed/VzoEXa?default-tab=html%2Cresult"
            frameBorder="no"
            loading="lazy"
            allowTransparency="true"
            allowFullScreen="true"
          />
        </section>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.1.3 - Удаление</h4>
        <p className={styles.description}>
          Для того чтобы удалить узел существуют два метода. Первый, более старый метод работающий во всех браузерах,
          позволяет удалить ребенка <code className={styles.code}>elem</code> из родителя{' '}
          <code className={styles.code}>parent</code>. В таком случае необходимо иметь ссылку как на родителя так и на
          ребенка.{' '}
        </p>
        <CopyBlock text={`parent.removeChild(elem)`} language="html" showLineNumbers theme={shadesOfPurple} />
        <p className={styles.description}>
          Более современный метод, но с гарантированной поддержкой только в новых браузерах, он вызывается на самом
          элементе <code className={styles.code}>elem</code> который необходимо удалить.{' '}
        </p>
        <CopyBlock text={`elem.remove()`} language="html" showLineNumbers theme={shadesOfPurple} />
        <p className={styles.description} />
        <iframe
          height="300"
          scrolling="no"
          title="module-08-removeChild"
          src="https://codepen.io/goit-fe-adv/embed/MvNBde?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.1.4 - Клонирование</h4>
        <p className={styles.description}>
          Представим что у нас есть элемент с текстом, и мы хотим вставить такой же элемент в другую часть документа. Мы
          уже знаем что каждый элемент может существовать в <code className={styles.code}>document</code> в одном
          экземпляре. Но элемент можно клонировать и работать с этим клоном (точной копией).{' '}
        </p>
        <p className={styles.description}>
          Так же мы могли бы создать новый элемент и работать с ним, но в ряде случаев гораздо эффективнее клонировать
          существующий, а потом изменить текст внутри. В частности, если элемент большой, то клонировать его будет
          быстрее, чем пересоздавать.{' '}
        </p>
        <CopyBlock text={`elem.cloneNode(true)`} language="html" showLineNumbers theme={shadesOfPurple} />
        <p className={styles.description}>
          Создаст глубокую копию элемента – вместе с атрибутами, включая все поддерево. Если же вызвать с аргументом
          <code className={styles.code}>false</code>, то копия будет сделана без дочерних элементов.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="module-08-cloneNode"
          src="https://codepen.io/goit-fe-adv/embed/JygaNZ?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.2 - Свойство innerHTML</h3>
      <p className={styles.description}>
        Еще один способ создать DOM-элементы и поместить их в дерево это использовать строки и позволить браузеру
        сделать всю тяжелую работу. Как мы увидим далее, у такого подхода есть свои плюсы и минусы.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.2.1 - Создание узлов</h4>
        <p className={styles.description}>
          <code className={styles.code}>elem.innerHTML</code> — свойство, позволяет получить содержимое элемента,
          включая теги, в виде строки. Значение, возвращаемое <code className={styles.code}>innerHTML</code> — всегда
          валидный HTML-код.{' '}
        </p>
        <p className={styles.description}>
          Оно доступно как для чтения так и для записи. Если записать в <code className={styles.code}>innerHTML</code>{' '}
          элемента строку с HTML-тегами, то браузер ее распарсит и превратит их в валидные DOM-узлы.{' '}
        </p>
        <CopyBlock
          text={`elem.innerHTML = '<p class="text">Pellentesque habitant.</p>';`}
          language="html"
          showLineNumbers
          theme={shadesOfPurple}
        />
        <p className={styles.description}>
          Такой код говорит браузеру распарсить строку, проверить на наличие тегов, если нашел таковые то создать
          DOM-элементы и вставить их в правильном порядке. При таком подходе, в отличии от{' '}
          <code className={styles.code}>createElement</code>, мы не получаем ссылку на созданный DOM-элемент. В то же
          время, создавать много разметки проще.{' '}
        </p>
        <p className={styles.notificationWarn}>
          Изменение <code className={styles.code}>innerHTML</code> полностью удалит и пересоздаст всех потомков
          контейнера. В результате мы получаем дополнительные затраты на сериализацию уже существующей разметки, что не
          очень хорошо.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="module-08-innerHTML"
          src="https://codepen.io/goit-fe-adv/embed/QMeVyj?default-tab=html%2Cresult"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.3 - Метод insertAdjacentHTML()</h3>
      <p className={styles.description}>
        Метод парсит указанную строку как HTML и добавляет результирующие узлы в указанное место DOM-дерева. Не делает
        повторный рендеринг для существующих элементов внутри элемента-родителя на котором используется. Это позволяет
        избежать дополнительного этапа сериализации, делая его быстрее, чем непосредственная манипуляция{' '}
        <code className={styles.code}>innerHTML</code>.{' '}
      </p>
      <CopyBlock
        text={`element.insertAdjacentHTML(position, string)`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
      <p className={styles.description}>
        <code className={styles.code}>position</code> — позиция относительно элемента. Принимает одно из следующих
        значений:{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>'beforebegin'</code> - перед element
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>'afterbegin'</code> - внутрь element, в самое начало контента
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>'beforeend'</code> - внутрь element, в самый конец контента
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>'afterend'</code> - после element
        </li>
      </ul>
      <img className={styles.img} src={insert_adjacentImg} alt="insertadjacent" />
      <p className={styles.notificationWarn}>
        <code className={styles.code}>beforebegin</code> и <code className={styles.code}>afterend</code> работают только
        в том случае, если узел уже находится в DOM-дереве и имеет родительский элемент.
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="module-08-insertAdjacent"
        src="https://codepen.io/goit-fe-adv/embed/jLgQyR?default-tab=html%2Cresult"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <p className={styles.description}>
        У этого метода есть братья-близнецы. Их синтаксис, за исключением последнего параметра, полностью совпадает с{' '}
        <code className={styles.code}>insertAdjacentHTML</code>. Вместе они образуют универсальный швейцарский нож для
        вставки чего угодно куда угодно.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>elem.insertAdjacentElement(position, elem)</code> — вставляет в произвольное
          место не HTML-строку, а элемент <code className={styles.code}>elem</code>.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>elem.insertAdjacentText(position, text)</code> — создаёт текстовый узел из
          строки <code className={styles.code}>text</code> и вставляет его в указанное место относительно{' '}
          <code className={styles.code}>elem</code>.
        </li>
      </ul>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>4.4 - Оптимизация работы с DOM</h3>
      <p className={styles.description}>
        Манипуляция DOM-дерева это дорогая операция. Необходимо всеми возможными методами стараться минимизировать
        количество обращений к DOM. Разберемся с очень важными концепциями при работе c DOM-деревом.{' '}
      </p>
      <a
        className={styles.link}
        rel="noopener noreferrer"
        target="_blank"
        href="https://www.youtube.com/watch?v=dndeRnzkJDU"
      >
        Как браузер рисует страницу
      </a>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.4.1 - Repaint</h4>
        <p className={styles.description}>
          Происходит когда изменения произошли в стилях элемента влияющих на внешний вид, но не на геометрию. Например{' '}
          <code className={styles.code}>opacity</code>, <code className={styles.code}>background-color</code>,{' '}
          <code className={styles.code}>visibility</code> и <code className={styles.code}>outline</code>. Браузер
          отрисовывает его заново, с учётом нового стиля. Это дорогая операция, потому что браузер проверяет видимость
          всех остальных узлов в дереве, один или более могут оказаться скрыты под изменившим внешний вид.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.4.2 - Reflow</h4>
        <p className={styles.description}>
          Происходит когда изменения затрагивают содержимое, структуру документа, положение элементов. Идет пересчет
          позиционирования и размеров всех элементов, что ведет к перерисовке части или всего документа. Изменения
          размера одного родительского контейнера повлияет на всех его детей и предков. Имеет значительно большее
          влияние на производительность чем <code className={styles.code}>repaint</code>.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.4.3 - Выводы</h4>
        <p className={styles.description}>
          Всегда необходимо думать об оптимизации работы с DOM. Все вышеперечисленные операции блокируют браузер.
          Страница не может выполнять никакие другие операции в то время, когда проиходит{' '}
          <code className={styles.code}>reflow</code> или <code className={styles.code}>repaint</code>. Причинами таких
          изменений обычно являются:{' '}
        </p>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            Манипуляции с DOM (добавление, удаление, изменение, перестановка элементов){' '}
          </li>
          <li className={styles.list__item}>Изменение содержимого, в т.ч. текста в полях форм </li>
          <li className={styles.list__item}>Расчёт или изменение CSS-свойств </li>
          <li className={styles.list__item}>Добавление и удаление таблиц стилей</li>
          <li className={styles.list__item}>
            Манипуляции с атрибутом <code className={styles.code}>class</code>{' '}
          </li>
          <li className={styles.list__item}>Манипуляции с окном браузера (изменения размеров, прокрутка)</li>
          <li className={styles.list__item}>
            Активация псевдо-классов (например <code className={styles.code}>:hover</code>)
          </li>
        </ul>
        <p className={styles.notificationWarn}>
          Современные браузеры оптимизируют процесс отрисовки страницы без вмешательства разработчика. И все же не
          забывайте думать о производительности, следуйте лучшим практикам из статей в дополнительных материалах этой
          секции.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>4.4.4 - Дополнительные материалы</h4>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              rel="noopener noreferrer"
              target="_blank"
              href="https://habr.com/ru/post/224187/"
            >
              Рендеринг WEB-страницы: что об этом должен знать front-end разработчик{' '}
            </a>
          </li>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              target="_blank"
              href="https://medium.com/swlh/what-the-heck-is-repaint-and-reflow-in-the-browser-b2d0fb980c08"
              rel="noopener noreferrer"
            >
              Understanding Repaint and Reflow in JavaScript
            </a>
          </li>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              target="_blank"
              rel="noopener noreferrer"
              href="https://developers.google.com/web/fundamentals/performance/rendering/avoid-large-complex-layouts-and-layout-thrashing"
            >
              Avoid Large, Complex Layouts and Layout Thrashing
            </a>
          </li>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.sitepoint.com/10-ways-minimize-reflows-improve-performance/"
            >
              10 Ways to Minimize Reflows and Improve Performance
            </a>
          </li>
        </ul>
      </section>
    </section>
  </section>
))
